import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Button, 
  Tile, 
  Tag,
  Section
} from '@carbon/react';
import {
  ArrowLeft,
  UserAvatar,
  Phone,
  Email,
  UserMultiple,
  Calendar,
  Time,
  ChevronDown,
  ChevronUp,
  Star
} from '@carbon/icons-react';
import PageContainer from '../components/PageContainer';

const ResidentFamily = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';

  const [expandedSections, setExpandedSections] = useState({
    visitHistory: false,
    plannedVisits: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mock data
  const familyData = {
    resident: {
      firstName: 'Milica',
      lastName: 'Jovanović',
      room: '101'
    },
    primaryGuardian: {
      firstName: 'Marko',
      lastName: 'Jovanović',
      relationship: 'Sin',
      phone: '+381 64 123 4567',
      email: 'marko.jovanovic@email.com'
    },
    additionalContacts: [
      { name: 'Ana Jovanović', relationship: 'Ćerka', phone: '+381 65 987 6543' },
      { name: 'Petar Jovanović', relationship: 'Unuk', phone: '+381 63 555 1234' },
      { name: 'Jovana Nikolić', relationship: 'Nećaka', phone: '+381 64 777 8888' }
    ],
    visitHistory: [
      { date: '03.11.2025', time: '14:30', visitor: 'Marko Jovanović', note: 'Doneo knjige i voće', duration: '2h' },
      { date: '01.11.2025', time: '10:00', visitor: 'Ana Jovanović', note: 'Rutinska poseta', duration: '1.5h' },
      { date: '28.10.2025', time: '16:00', visitor: 'Petar Jovanović', note: 'Kratak razgovor', duration: '45min' },
      { date: '25.10.2025', time: '11:30', visitor: 'Marko Jovanović', note: 'Zakazao lekarski pregled', duration: '2h' }
    ],
    plannedVisits: [
      { date: '08.11.2025', time: '15:00', visitor: 'Marko Jovanović', note: 'Rođendan' },
      { date: '10.11.2025', time: '11:00', visitor: 'Ana Jovanović', note: 'Rutinska nedeljna poseta' }
    ]
  };

  return (
    <PageContainer>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <Button
          kind="ghost"
          size="sm"
          renderIcon={ArrowLeft}
          hasIconOnly
          iconDescription="Nazad"
          onClick={() => navigate(`/residents/${id}`)}
        />
        
        <div 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: isDarkMode ? '#2d2d2d' : 'white',
            borderRadius: '4px',
            border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => navigate(`/residents/${id}`)}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
        >
          <div style={{
            width: '32px',
            height: '32px',
            borderRadius: '4px',
            overflow: 'hidden',
            flexShrink: 0,
            backgroundColor: 'rgba(159, 183, 70, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <UserAvatar size={20} style={{ color: '#9fb746' }} />
          </div>
          <div>
            <div style={{ 
              fontSize: '0.875rem', 
              fontWeight: '600',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {familyData.resident.firstName} {familyData.resident.lastName}
            </div>
            <div style={{ 
              fontSize: '0.7rem', 
              opacity: 0.7,
              color: isDarkMode ? '#d0d0d0' : 'inherit'
            }}>
              Soba {familyData.resident.room}
            </div>
          </div>
        </div>
      </div>

      {/* Primarni staratelj */}
      <Tile style={{ 
        marginBottom: '1rem',
        padding: '1.25rem',
        border: `2px solid ${isDarkMode ? 'rgba(159, 183, 70, 0.3)' : 'rgba(159, 183, 70, 0.2)'}`,
        background: isDarkMode 
          ? 'linear-gradient(135deg, rgba(159, 183, 70, 0.05) 0%, #2d2d2d 100%)'
          : 'linear-gradient(135deg, rgba(159, 183, 70, 0.03) 0%, white 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <Star size={20} style={{ color: '#9fb746' }} />
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
            Primarni staratelj
          </h3>
        </div>
        
        <div style={{ 
          display: 'grid',
          gap: '0.75rem'
        }}>
          <div>
            <div style={{ 
              fontSize: '1.125rem', 
              fontWeight: '600',
              marginBottom: '0.25rem',
              color: isDarkMode ? '#f4f4f4' : 'inherit'
            }}>
              {familyData.primaryGuardian.firstName} {familyData.primaryGuardian.lastName}
            </div>
            <Tag size="sm" type="green">{familyData.primaryGuardian.relationship}</Tag>
          </div>
          
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '0.5rem',
            paddingTop: '0.5rem',
            borderTop: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Phone size={16} style={{ color: '#9fb746', flexShrink: 0 }} />
              <span style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                {familyData.primaryGuardian.phone}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Email size={16} style={{ color: '#9fb746', flexShrink: 0 }} />
              <span style={{ fontSize: '0.8125rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                {familyData.primaryGuardian.email}
              </span>
            </div>
          </div>
        </div>
      </Tile>

      {/* Dodatni kontakti */}
      <Tile style={{ 
        marginBottom: '1rem',
        padding: '1.25rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <UserMultiple size={20} style={{ color: '#9fb746' }} />
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: isDarkMode ? '#f4f4f4' : 'inherit' }}>
            Dodatni kontakti
          </h3>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {familyData.additionalContacts.map((contact, idx) => (
            <div 
              key={idx}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0.625rem',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '4px',
                border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'}`,
                gap: '1rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '150px' }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(159, 183, 70, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <UserAvatar size={16} style={{ color: '#9fb746' }} />
                </div>
                <div>
                  <div style={{ 
                    fontSize: '0.8125rem', 
                    fontWeight: '600',
                    color: isDarkMode ? '#f4f4f4' : 'inherit'
                  }}>
                    {contact.name}
                  </div>
                  <div style={{ 
                    fontSize: '0.7rem', 
                    opacity: 0.6,
                    color: isDarkMode ? '#d0d0d0' : 'inherit'
                  }}>
                    {contact.relationship}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <Phone size={14} style={{ color: '#9fb746' }} />
                <span style={{ fontSize: '0.75rem', color: isDarkMode ? '#e8e8e8' : 'inherit' }}>
                  {contact.phone}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Tile>

      {/* Istorija poseta */}
      <Section style={{ marginBottom: '1rem' }}>
        <Tile style={{ padding: '0' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.visitHistory ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('visitHistory')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
              <Calendar size={18} style={{ color: '#9fb746' }} />
              Istorija poseta
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.visitHistory ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('visitHistory');
              }}
            >
              {expandedSections.visitHistory ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.visitHistory ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.visitHistory 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.visitHistory ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {familyData.visitHistory.map((visit, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                      borderLeft: '3px solid #9fb746',
                      borderRadius: '4px',
                      display: 'grid',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'start',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: '0.8125rem', 
                          fontWeight: '600',
                          marginBottom: '0.25rem',
                          color: isDarkMode ? '#f4f4f4' : 'inherit'
                        }}>
                          {visit.visitor}
                        </div>
                        <div style={{ 
                          fontSize: '0.7rem', 
                          opacity: 0.6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: isDarkMode ? '#d0d0d0' : 'inherit'
                        }}>
                          <span>{visit.date}</span>
                          <span>•</span>
                          <span>{visit.time}</span>
                          <span>•</span>
                          <span>{visit.duration}</span>
                        </div>
                      </div>
                    </div>
                    {visit.note && (
                      <div style={{ 
                        fontSize: '0.75rem',
                        opacity: 0.8,
                        fontStyle: 'italic',
                        color: isDarkMode ? '#e8e8e8' : 'inherit'
                      }}>
                        {visit.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>

      {/* Planirane posete */}
      <Section style={{ marginBottom: '1rem' }}>
        <Tile style={{ padding: '0' }}>
          <div 
            style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '0.75rem',
              borderBottom: expandedSections.plannedVisits ? `1px solid ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` : 'none',
              cursor: 'pointer'
            }}
            onClick={() => toggleSection('plannedVisits')}
          >
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '600' }}>
              <Time size={18} style={{ color: '#9fb746' }} />
              Planirane posete
            </h3>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              iconDescription={expandedSections.plannedVisits ? "Zatvori" : "Otvori"}
              onClick={(e) => {
                e.stopPropagation();
                toggleSection('plannedVisits');
              }}
            >
              {expandedSections.plannedVisits ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </Button>
          </div>
          
          <div style={{
            maxHeight: expandedSections.plannedVisits ? '2000px' : '0',
            overflow: 'hidden',
            transition: expandedSections.plannedVisits 
              ? 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-in-out' 
              : 'max-height 0.2s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.15s ease-in-out',
            opacity: expandedSections.plannedVisits ? 1 : 0
          }}>
            <div style={{ padding: '0.75rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {familyData.plannedVisits.map((visit, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '0.75rem',
                      backgroundColor: isDarkMode ? 'rgba(159, 183, 70, 0.1)' : 'rgba(159, 183, 70, 0.05)',
                      border: '1px solid rgba(159, 183, 70, 0.3)',
                      borderRadius: '4px',
                      display: 'grid',
                      gap: '0.5rem'
                    }}
                  >
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'start',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }}>
                      <div>
                        <div style={{ 
                          fontSize: '0.8125rem', 
                          fontWeight: '600',
                          marginBottom: '0.25rem',
                          color: isDarkMode ? '#f4f4f4' : 'inherit'
                        }}>
                          {visit.visitor}
                        </div>
                        <div style={{ 
                          fontSize: '0.7rem', 
                          opacity: 0.6,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          color: isDarkMode ? '#d0d0d0' : 'inherit'
                        }}>
                          <span>{visit.date}</span>
                          <span>•</span>
                          <span>{visit.time}</span>
                        </div>
                      </div>
                      <Tag size="sm" type="green">Planirana</Tag>
                    </div>
                    {visit.note && (
                      <div style={{ 
                        fontSize: '0.75rem',
                        opacity: 0.8,
                        fontStyle: 'italic',
                        color: isDarkMode ? '#e8e8e8' : 'inherit'
                      }}>
                        {visit.note}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Tile>
      </Section>
    </PageContainer>
  );
};

export default ResidentFamily;
